import { createContext, ReactNode, useContext, useState } from 'react'
import { GroupDoc } from 'types/api/changes'
import { getUniqueRandomColors } from 'utils/colors'

interface AddGroupProviderProps {
  children: ReactNode
}

export interface AddGroupContextProps {
  color: string
  setColor: (color: string) => void
  title: string
  setTitle: (title: string) => void
  isCalendarChannel: boolean
  setIsCalendarChannel: (isChannel: boolean) => void
  link: string
  setLink: (link: string) => void
  setPhoto: (data: string) => void
  photo: string
  metaDataState: string
  setMetaDataState: (metaData: string) => void
  allCanAddItemsState: string
  setAllCanAddItemsState: (allCanAddItems: string) => void
  addParticipantsState: string
  setAddParticipantsState: (addParticipants: string) => void
  eventOwnershipState: string
  setEventOwnershipState: (eventOwnership: string) => void
  colors: string[]
  eventsDetailsState: string
  setEventsDetailsState: (eventDetails: string) => void
  createdGroup: GroupDoc | undefined
  setCreatedGroup: (group: GroupDoc | undefined) => void
}

const AddGroupContext = createContext<AddGroupContextProps>({
  color: '',
  setColor: () => {},
  title: '',
  setTitle: () => {},
  isCalendarChannel: false,
  setIsCalendarChannel: () => {},
  link: '',
  setLink: () => {},
  setPhoto: () => {},
  photo: '',
  metaDataState: '1',
  setMetaDataState: () => {},
  allCanAddItemsState: '1',
  setAllCanAddItemsState: () => {},
  addParticipantsState: '1',
  setAddParticipantsState: () => {},
  eventOwnershipState: '0',
  setEventOwnershipState: () => {},
  eventsDetailsState: '0',
  setEventsDetailsState: () => {},
  colors: [],
  createdGroup: undefined,
  setCreatedGroup: () => {}
})

export default function AddGroupProvider(props: AddGroupProviderProps) {
  const [colors, setColors] = useState(getUniqueRandomColors(4))
  const [groupColor, setGroupColor] = useState(colors[0]!)
  const [title, setTitle] = useState('')
  const [channel, setChannel] = useState(false)
  const [link, setLink] = useState<string>('')

  const [metaDataState, setMetaDataState] = useState('1')
  const [allCanAddItemsState, setAllCanAddItemsState] = useState('1')
  const [addParticipantsState, setAddParticipantsState] = useState('1')
  const [eventOwnershipState, setEventOwnershipState] = useState('0')
  const [eventsDetailsState, setEventsDetailsState] = useState('0')

  const [photo, setPhoto] = useState('')

  const [createdGroup, setCreatedGroup] = useState<GroupDoc | undefined>(
    undefined
  )

  const onGroupColor = (color: string) => {
    setGroupColor(color)
  }
  const onTitle = (title: string) => {
    setTitle(title)
  }
  const onChannel = (isChannel: boolean) => {
    setChannel(isChannel)
  }
  const onSetLink = (link: string) => {
    setLink(link)
  }
  const onSetPhoto = (photo: string) => {
    setPhoto(photo)
  }

  const context: AddGroupContextProps = {
    color: groupColor,
    setColor: onGroupColor,
    title: title,
    setTitle: onTitle,
    isCalendarChannel: channel,
    setIsCalendarChannel: onChannel,
    link: link,
    setLink: onSetLink,
    photo: photo,
    setPhoto: onSetPhoto,
    metaDataState: metaDataState,
    setMetaDataState: setMetaDataState,
    allCanAddItemsState: allCanAddItemsState,
    setAllCanAddItemsState: setAllCanAddItemsState,
    addParticipantsState: addParticipantsState,
    setAddParticipantsState: setAddParticipantsState,
    eventOwnershipState: eventOwnershipState,
    setEventOwnershipState: setEventOwnershipState,
    colors: colors,
    eventsDetailsState: eventsDetailsState,
    setEventsDetailsState: setEventsDetailsState,
    createdGroup: createdGroup,
    setCreatedGroup: setCreatedGroup
  }

  return (
    <AddGroupContext.Provider value={context}>
      {props.children}
    </AddGroupContext.Provider>
  )
}

export function useAddGroup(): AddGroupContextProps {
  return useContext(AddGroupContext)
}
